/**
 * Meta Pixel (browser) + CAPI payloads for dentoxcellence.com
 *
 * Event map (client spec):
 * PageView          — index.html on load
 * ViewContent       — formation detail page
 * Lead              — "S'inscrire" click + any form submit
 * Contact           — WhatsApp click
 * CompleteRegistration — inscription form confirmed (server OK)
 * Purchase          — reserved for future online payment
 */

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://dentoxcellence-backend.dentoxcellence.workers.dev/api';

function isPixelAvailable() {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

export function createMetaEventId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function getFormationContentId(formation, specialtySlug) {
  const courseKey = formation?.slug || formation?.id;
  if (specialtySlug && courseKey) return `${specialtySlug}_${courseKey}`;
  return courseKey || formation?.title || 'formation';
}

function getCookie(name) {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function getMetaContext() {
  return {
    metaFbp: getCookie('_fbp'),
    metaFbc: getCookie('_fbc'),
    metaSourceUrl: typeof window !== 'undefined' ? window.location.href : '',
  };
}

function trackEvent(eventName, params = {}, eventId) {
  if (!isPixelAvailable()) return null;
  if (eventId) {
    window.fbq('track', eventName, params, { eventID: eventId });
  } else {
    window.fbq('track', eventName, params);
  }
  return eventId;
}

function sendMetaCapiBeacon(payload) {
  if (typeof window === 'undefined') return;
  try {
    fetch(`${API_BASE}/meta/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...getMetaContext(), ...payload }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore
  }
}

function formationParams({ formationId, formationName, specialtyName, source, label } = {}) {
  return {
    content_ids: formationId ? [formationId] : undefined,
    content_name: formationName || label,
    content_category: specialtyName || 'Formation',
    ...(source ? { source } : {}),
  };
}

export function trackViewContent({ formationId, formationName, specialtyName } = {}) {
  trackEvent('ViewContent', {
    ...formationParams({ formationId, formationName, specialtyName }),
    content_type: 'training',
  });
}

export function trackLeadIntent({ formationId, formationName, source } = {}) {
  const eventId = createMetaEventId();
  trackEvent(
    'Lead',
    {
      ...formationParams({ formationId, formationName, source }),
      lead_event: 'intent',
    },
    eventId
  );
  return eventId;
}

/**
 * Fire browser Lead + return payload for server CAPI deduplication.
 */
export function prepareLeadSubmitTracking({
  formationId,
  formationName,
  source,
} = {}) {
  const eventId = createMetaEventId();
  trackEvent(
    'Lead',
    {
      ...formationParams({ formationId, formationName, source }),
      lead_event: 'form_submit',
    },
    eventId
  );

  try {
    sessionStorage.setItem('meta_last_lead_event_id', eventId);
    sessionStorage.setItem('meta_last_lead_at', String(Date.now()));
  } catch {
    // ignore
  }

  return {
    metaEventId: eventId,
    ...getMetaContext(),
    ...(formationId ? { metaFormationId: formationId } : {}),
  };
}

/**
 * Inscription forms: Lead on submit + CompleteRegistration after server confirms.
 */
export function prepareInscriptionSubmitTracking({
  formationId,
  formationName,
  source,
} = {}) {
  const leadPayload = prepareLeadSubmitTracking({ formationId, formationName, source });
  return {
    ...leadPayload,
    metaCompleteRegistrationEventId: createMetaEventId(),
  };
}

export function trackCompleteRegistration({
  eventId,
  formationId,
  formationName,
  source,
} = {}) {
  const id = eventId || createMetaEventId();
  trackEvent(
    'CompleteRegistration',
    {
      ...formationParams({ formationId, formationName, source }),
      status: 'confirmed',
    },
    id
  );
  return id;
}

export function trackLeadSubmit({ formationId, formationName, source } = {}) {
  return prepareLeadSubmitTracking({ formationId, formationName, source });
}

export function trackContact({ formationId, formationName, source, label } = {}) {
  const eventId = createMetaEventId();
  trackEvent('Contact', formationParams({ formationId, formationName, source, label }), eventId);
  sendMetaCapiBeacon({
    eventName: 'Contact',
    metaEventId: eventId,
    metaFormationId: formationId,
    formationName: formationName || label,
  });
  return eventId;
}

/** Reserved — call when an online payment flow is added. */
export function trackPurchase({ value, currency = 'DZD', formationId, formationName, source } = {}) {
  const eventId = createMetaEventId();
  trackEvent(
    'Purchase',
    {
      ...formationParams({ formationId, formationName, source }),
      value,
      currency,
    },
    eventId
  );
  sendMetaCapiBeacon({
    eventName: 'Purchase',
    metaEventId: eventId,
    metaFormationId: formationId,
    formationName,
    value,
    currency,
  });
  return eventId;
}

export function isWhatsAppUrl(url) {
  return typeof url === 'string' && url.includes('wa.me');
}
